import React from 'react';
import Button from '@ramsey-design-system/button';
import ProgressBar from '@ramsey-design-system/progress-bar';
import { LessonData } from '../../types';

// Styles
import '@ramsey-design-system/button/dist/button.css';
import '@ramsey-design-system/progress-bar/dist/progress-bar.css';
import styles from './user-overview.module.css';
import getCurrentActionItem from '../../lib/getCurrentActionItem';

type UserOverviewProps = {
  lessonData: LessonData;
  urlPrefix?: string;
};

type LessonProgressProps = {
  completedSteps: number;
  totalSteps: number;
};

function LessonProgress({ completedSteps, totalSteps }: LessonProgressProps) {
  const progressValue = Math.floor((completedSteps / totalSteps) * 100);
  const currentStep =
    completedSteps < totalSteps ? completedSteps + 1 : totalSteps;

  return (
    <div className={styles.progress_container}>
      <div className={styles.step_count} data-testid="current-step">
        Step {currentStep} of {totalSteps}
      </div>
      <div className={styles.progress}>
        <ProgressBar value={progressValue} color="success" />
        <div className={styles.progress_value} data-testid="progress-value">
          {progressValue}%
        </div>
      </div>
    </div>
  );
}

function UserOverview({ lessonData, urlPrefix = '/learn' }: UserOverviewProps) {
  if (!lessonData) return null;

  const {
    id: lesson_id,
    course_id,
    title,
    subtitle,
    thumbnail,
    total_items,
    completed_items,
    action_groups,
  } = lessonData;

  const currentActionItem = getCurrentActionItem(action_groups);
  const currentActionItemId =
    currentActionItem?.template === 'lesson-video' ? '' : currentActionItem?.id;
  const currentActionItemUrl = `${urlPrefix}/${course_id}/${lesson_id}/${currentActionItemId}`;

  const buttonText =
    completed_items > 0 ? `Resume ${subtitle}` : `Start ${subtitle}`;

  const dataLoggerElementId = `${buttonText} Button`;
  const dataLoggerElementLabel = buttonText;

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        <div className={styles.main}>
          <div className={styles.lesson_details}>
            <div>
              <div className={styles.lesson_count}>{subtitle} of 9</div>
              <h2 className={styles.heading}>{title}</h2>
            </div>

            <LessonProgress
              completedSteps={completed_items}
              totalSteps={total_items}
            />
          </div>
          <Button
            key={buttonText}
            element={'a'}
            href={currentActionItemUrl}
            className="rds-Button--lap-and-up"
            data-testid="action-button"
            data-logger-element-id={dataLoggerElementId}
            data-logger-element-label={dataLoggerElementLabel}
          >
            {buttonText}
          </Button>
        </div>

        <div>
          <img className={styles.thumbnail} src={thumbnail} alt="thumbnail" />
        </div>
      </div>

      <Button
        key={buttonText}
        element={'a'}
        href={currentActionItemUrl}
        fullWidth
        className="rds-Button--palm"
        data-logger-element-id={dataLoggerElementId}
        data-logger-element-label={dataLoggerElementLabel}
      >
        {buttonText}
      </Button>
    </div>
  );
}

export default UserOverview;
export { LessonProgress };
